/**
 * Member Detail page — the household-detail view for an existing member.
 *
 * Mirrors NonMemberDetailPage (intentionally similar layout & affordances) but for
 * club members. The URL is canonical for the HOUSEHOLD: /members/:id resolves to
 * the primary's household even if :id is a dependent (the backend follows
 * primary_member_id; focus_id is echoed back so we highlight the dependent row).
 *
 * MVP scope: profile, household roster (members + non-member family), inline edit
 * for the primary's profile, add/remove family member, lock/unlock, reset password.
 * Activity history (enrollments/check-ins) is intentionally deferred.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import memberService, { HouseholdRosterEntry } from '../api/services/memberService';
import { Member } from '../types';
import { useAppSelector } from '../hooks/useAppSelector';
import { useSnackbar } from '../components/common/SnackbarProvider';
import AddFamilyMemberDialog from '../components/AddFamilyMemberDialog';

// Title-case a snake-or-lower string for display ("non_member_dependent" → "Non Member Dependent",
// "child" → "Child"). The backend stores enum values lowercase; the chip-shaped UI wants Title Case.
function titleCase(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function ageFromDob(dob: string | null): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
}

interface EditForm {
  first_name: string;
  last_name: string;
  member_number: string;
  email: string;
  mobile_phone: string;
  home_phone: string;
  date_of_birth: string;
  joined_date: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
}

const EMPTY_EDIT: EditForm = {
  first_name: '',
  last_name: '',
  member_number: '',
  email: '',
  mobile_phone: '',
  home_phone: '',
  date_of_birth: '',
  joined_date: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
};

const MemberDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const user = useAppSelector((s) => s.auth.user);
  const canManage = user?.role === 'admin' || user?.role === 'superuser';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [primary, setPrimary] = useState<Member | null>(null);
  const [household, setHousehold] = useState<HouseholdRosterEntry[]>([]);
  const [focusId, setFocusId] = useState<string | null>(null);

  // Edit primary dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>(EMPTY_EDIT);
  const [editBusy, setEditBusy] = useState(false);

  // Add family member dialog
  const [addFamilyOpen, setAddFamilyOpen] = useState(false);

  // Remove confirmation
  const [removeTarget, setRemoveTarget] = useState<HouseholdRosterEntry | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await memberService.getMemberHousehold(id);
      setPrimary(resp.data.primary);
      setHousehold(resp.data.household);
      setFocusId(resp.data.focus_id);
    } catch (e) {
      type ErrLike = { response?: { data?: { error?: string; message?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to load member');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  function openEdit() {
    if (!primary) return;
    setEditForm({
      first_name: primary.first_name || '',
      last_name: primary.last_name || '',
      member_number: (primary as any).member_number || '',
      email: primary.email || '',
      mobile_phone: (primary as any).mobile_phone || '',
      home_phone: (primary as any).home_phone || '',
      date_of_birth: (primary as any).date_of_birth || '',
      joined_date: (primary as any).joined_date || '',
      address_line1: (primary as any).address_line1 || '',
      address_line2: (primary as any).address_line2 || '',
      city: (primary as any).city || '',
      state: (primary as any).state || '',
      postal_code: (primary as any).postal_code || '',
    });
    setEditOpen(true);
  }

  async function saveEdit() {
    if (!primary) return;
    setEditBusy(true);
    try {
      await axiosInstance.put(`/members/${primary.id}`, {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        // Account number is staff-only on the backend (honored only in admin
        // context). Send it just when staff edits; blank is ignored server-side.
        ...(canManage && editForm.member_number.trim()
          ? { member_number: editForm.member_number.trim() }
          : {}),
        email: editForm.email.trim().toLowerCase(),
        mobile_phone: editForm.mobile_phone.trim() || null,
        home_phone: editForm.home_phone.trim() || null,
        date_of_birth: editForm.date_of_birth || null,
        joined_date: editForm.joined_date || null,
        address_line1: editForm.address_line1.trim() || null,
        address_line2: editForm.address_line2.trim() || null,
        city: editForm.city.trim() || null,
        state: editForm.state.trim() || null,
        postal_code: editForm.postal_code.trim() || null,
      });
      snackbar.success('Profile updated.');
      setEditOpen(false);
      await load();
    } catch (e) {
      type ErrLike = { response?: { data?: { error?: string; message?: string } } };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.response?.data?.message || 'Update failed');
    } finally {
      setEditBusy(false);
    }
  }

  async function toggleLock() {
    if (!primary) return;
    const path = (primary as any).is_locked ? 'unlock' : 'lock';
    try {
      await axiosInstance.post(`/members/${primary.id}/${path}`);
      snackbar.success((primary as any).is_locked ? 'Account unlocked.' : 'Account locked.');
      await load();
    } catch (e) {
      type ErrLike = { response?: { data?: { error?: string } } };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || 'Lock/unlock failed');
    }
  }

  async function resetPassword() {
    if (!primary) return;
    if (!window.confirm(`Reset password for ${primary.first_name} ${primary.last_name}? A new temporary password will be returned.`)) return;
    try {
      const resp = await axiosInstance.post<{ data?: { temporary_password?: string }; temporary_password?: string }>(`/members/${primary.id}/reset-password`);
      const temp = resp.data.data?.temporary_password || resp.data.temporary_password;
      if (temp) {
        window.alert(`Temporary password: ${temp}\n\nPlease share securely.`);
      } else {
        snackbar.success('Password reset.');
      }
    } catch (e) {
      type ErrLike = { response?: { data?: { error?: string } } };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || 'Password reset failed');
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    setRemoveBusy(true);
    try {
      if (removeTarget.kind === 'non_member_dependent') {
        // NonMemberFamily lives on its own endpoint
        await axiosInstance.delete(`/non-members/family/${removeTarget.id}`);
      } else {
        // Member (incl. dependents): soft-delete (deactivate). Permanent purge stays an admin/superuser action elsewhere.
        await axiosInstance.delete(`/members/${removeTarget.id}`);
      }
      snackbar.success(`Removed ${removeTarget.full_name}.`);
      setRemoveTarget(null);
      await load();
    } catch (e) {
      type ErrLike = { response?: { data?: { error?: string } } };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || 'Remove failed');
    } finally {
      setRemoveBusy(false);
    }
  }

  const primaryFullName = useMemo(
    () => (primary ? `${primary.first_name || ''} ${primary.last_name || ''}`.trim() : ''),
    [primary],
  );

  // Back: prefer browser-history (wherever they came from) and fall back to
  // /admin if this was a deep-link with no history. We intentionally don't hard-
  // code /admin/members — there is no such route; the admin page is at /admin
  // and remembers the active list via Redux (managementView).
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/admin');
  };

  const statusValue = primary ? String((primary as any).membership_status || '').toLowerCase() : '';
  const statusColor: 'success' | 'warning' | 'error' | 'default' =
    statusValue === 'active'
      ? 'success'
      : statusValue.startsWith('non_member')
        ? 'warning'
        : statusValue === 'suspended' || statusValue === 'cancelled' || statusValue === 'expired'
          ? 'error'
          : 'default';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !primary) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error || 'Member not found'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: 4 }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack}>
          Back
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {primaryFullName}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
            {(primary as any).member_number && (
              <Chip size="small" label={`#${(primary as any).member_number}`} sx={{ fontFamily: 'monospace' }} />
            )}
            {statusValue && (
              <Chip size="small" color={statusColor} label={titleCase(statusValue.replace('non_member_', ''))} />
            )}
            {(primary as any).is_locked && <Chip size="small" color="error" icon={<LockIcon />} label="Locked" />}
            {(primary as any).is_active === false && <Chip size="small" color="default" label="Deactivated" />}
          </Stack>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => void load()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Grid container spacing={2}>
        {/* Left: profile card */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                  {(primary.first_name || '?')[0]}
                  {(primary.last_name || '')[0]}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                    {primaryFullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {primary.email}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 1 }} />

              <Stack spacing={1.2}>
                {(primary as any).mobile_phone && (
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{(primary as any).mobile_phone}</Typography>
                    <Typography variant="caption" color="text.secondary">mobile</Typography>
                  </Stack>
                )}
                {(primary as any).home_phone && (
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{(primary as any).home_phone}</Typography>
                    <Typography variant="caption" color="text.secondary">home</Typography>
                  </Stack>
                )}
                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                  <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                  <Box>
                    <Typography variant="body2">
                      {[(primary as any).address_line1, (primary as any).address_line2].filter(Boolean).join(', ') || '—'}
                    </Typography>
                    <Typography variant="body2">
                      {[(primary as any).city, (primary as any).state, (primary as any).postal_code].filter(Boolean).join(', ') || '—'}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <CalendarTodayIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Joined {formatDate((primary as any).joined_date)}
                  </Typography>
                </Stack>
                {(primary as any).date_of_birth && (
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      Born {formatDate((primary as any).date_of_birth)}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        (age {ageFromDob((primary as any).date_of_birth) ?? '?'})
                      </Typography>
                    </Typography>
                  </Stack>
                )}
              </Stack>

              {canManage && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1}>
                    <Button startIcon={<EditIcon />} variant="outlined" fullWidth onClick={openEdit}>
                      Edit profile
                    </Button>
                    <Stack direction="row" spacing={1}>
                      <Button
                        startIcon={(primary as any).is_locked ? <LockOpenIcon /> : <LockIcon />}
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => void toggleLock()}
                      >
                        {(primary as any).is_locked ? 'Unlock' : 'Lock'}
                      </Button>
                      <Button
                        startIcon={<VpnKeyIcon />}
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => void resetPassword()}
                      >
                        Reset PW
                      </Button>
                    </Stack>
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right: household roster */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="h6">Household</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {household.length} {household.length === 1 ? 'person' : 'people'}
                  </Typography>
                </Box>
                {canManage && (
                  <Button
                    startIcon={<PersonAddIcon />}
                    variant="contained"
                    size="small"
                    onClick={() => setAddFamilyOpen(true)}
                  >
                    Add family
                  </Button>
                )}
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Relationship</TableCell>
                      <TableCell>DOB</TableCell>
                      <TableCell>Type</TableCell>
                      {canManage && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {household.map((p) => {
                      const focused = focusId === p.id;
                      const age = ageFromDob(p.date_of_birth);
                      return (
                        <TableRow
                          key={`${p.kind}-${p.id}`}
                          hover
                          sx={{
                            bgcolor: focused ? 'action.selected' : undefined,
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: p.is_primary ? 600 : 400 }}>
                                {p.full_name}
                              </Typography>
                              {p.is_primary && <Chip size="small" color="primary" label="Primary" />}
                            </Stack>
                            {p.email && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {p.email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{titleCase(p.relationship_to_primary) || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {p.date_of_birth ? formatDate(p.date_of_birth) : '—'}
                              {age != null && (
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {' '}(age {age})
                                </Typography>
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={p.is_member ? 'success' : 'warning'}
                              label={p.is_member ? 'Member' : 'Non-member'}
                            />
                          </TableCell>
                          {canManage && (
                            <TableCell align="right">
                              {!p.is_primary && (
                                <Tooltip title={`Remove ${p.full_name}`}>
                                  <IconButton size="small" color="error" onClick={() => setRemoveTarget(p)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit primary profile */}
      <Dialog open={editOpen} onClose={() => !editBusy && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit profile</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 6 }}>
              <TextField label="First name" required fullWidth value={editForm.first_name}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField label="Last name" required fullWidth value={editForm.last_name}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
            </Grid>
            {canManage && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Account number"
                  fullWidth
                  value={editForm.member_number}
                  onChange={(e) => setEditForm({ ...editForm, member_number: e.target.value })}
                  helperText="Club account/member number. Staff only — leave unchanged to keep the current number."
                  sx={{ '& input': { fontFamily: 'monospace' } }}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField label="Email" type="email" required fullWidth value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField label="Mobile phone" fullWidth value={editForm.mobile_phone}
                onChange={(e) => setEditForm({ ...editForm, mobile_phone: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField label="Home phone" fullWidth value={editForm.home_phone}
                onChange={(e) => setEditForm({ ...editForm, home_phone: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField type="date" label="Date of birth" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                value={editForm.date_of_birth}
                onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField type="date" label="Joined date" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                value={editForm.joined_date}
                onChange={(e) => setEditForm({ ...editForm, joined_date: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Address line 1" fullWidth value={editForm.address_line1}
                onChange={(e) => setEditForm({ ...editForm, address_line1: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Address line 2" fullWidth value={editForm.address_line2}
                onChange={(e) => setEditForm({ ...editForm, address_line2: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 5 }}>
              <TextField label="City" fullWidth value={editForm.city}
                onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <TextField label="State" fullWidth value={editForm.state}
                onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField label="ZIP" fullWidth value={editForm.postal_code}
                onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editBusy}>Cancel</Button>
          <Button onClick={() => void saveEdit()} variant="contained" disabled={editBusy}>
            {editBusy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add family member */}
      <AddFamilyMemberDialog
        open={addFamilyOpen}
        primaryId={primary.id}
        primaryName={primaryFullName}
        onClose={() => setAddFamilyOpen(false)}
        onAdded={() => void load()}
      />

      {/* Confirm remove */}
      <Dialog open={!!removeTarget} onClose={() => !removeBusy && setRemoveTarget(null)}>
        <DialogTitle>Remove {removeTarget?.full_name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {removeTarget?.kind === 'non_member_dependent'
              ? 'This will permanently remove this non-member family dependent.'
              : 'This will deactivate the member (soft delete). A superuser can permanently delete from the management list.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)} disabled={removeBusy}>Cancel</Button>
          <Button onClick={() => void confirmRemove()} color="error" variant="contained" disabled={removeBusy}>
            {removeBusy ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MemberDetailPage;
